import { useCallback, useEffect, useState } from 'react';

// Open state for a dropdown that must close on Escape wherever focus is.
// NextUI only handles Escape when focus is inside the menu, which a wrapping
// Tooltip can prevent, so the document listener is the fallback.
//
// Closing hands focus back to the trigger, and after Escape NextUI treats that
// as keyboard focus and draws its focus outline. The trigger is marked with
// data-menu-closed until it loses focus, and index.css hides the outline in
// that state only; tabbing to the trigger later still shows it.
export const useEscapeClosableDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuJustClosed, setMenuJustClosed] = useState(false);

  const onOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    setMenuJustClosed(!open);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onOpenChange(false);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onOpenChange]);

  return {
    dropdownProps: { isOpen, onOpenChange },
    triggerProps: {
      'data-menu-closed': menuJustClosed || undefined,
      onBlur: () => setMenuJustClosed(false),
    },
  };
};
